import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, ErrorState, Switch } from '@bb/ui';
import { ApiError, type ApprovalPolicy, type MessagingRule } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';

const ROLES = ['reviewer', 'admin', 'owner', 'publisher'] as const;
const CHANNELS = ['push', 'email', 'sms', 'content_card', 'in_app', 'webhook'] as const;
const describe = (r: MessagingRule) =>
  r.kind === 'audience_size_over' ? `Audience over ${r.threshold.toLocaleString('en')} needs ${r.requiresRole}` : r.kind === 'channel' ? `Channel ${r.channel} needs ${r.requiresRole}` : r.kind === 'tag' ? `Tag needs ${r.requiresRole}` : 'Transactional campaigns launch without approval';

/** Settings → Approvals (BB-106): which objects need an approved revision, self-approval and messaging rules. */
export function ApprovalSettingsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/approvals' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/approvals' });
  const queryClient = useQueryClient();
  const canManage = usePermissions().has('env.manage');
  const view = useQuery({ queryKey: ['approval-policy', environmentId], queryFn: () => api.approvalPolicy(environmentId) });
  const [policy, setPolicy] = useState<ApprovalPolicy | null>(null);
  useEffect(() => {
    if (view.data) setPolicy(view.data.policy);
  }, [view.data]);
  const [kind, setKind] = useState<'audience_size_over' | 'channel' | 'transactional_exemption'>('audience_size_over');
  const [threshold, setThreshold] = useState('100000');
  const [channel, setChannel] = useState<(typeof CHANNELS)[number]>('sms');
  const [role, setRole] = useState<(typeof ROLES)[number]>('reviewer');
  const save = useMutation({ mutationFn: (next: ApprovalPolicy) => api.setApprovalPolicy(environmentId, next), onSuccess: (saved) => queryClient.setQueryData(['approval-policy', environmentId], saved) });
  if (view.isError) return <ErrorState title="Approval settings could not be loaded" message={view.error instanceof ApiError ? view.error.message : 'Something went wrong.'} />;
  if (!policy || !view.data) return <p aria-busy="true">Loading approval settings…</p>;
  const change = (next: ApprovalPolicy) => {
    setPolicy(next);
    save.mutate(next);
  };
  const addRule = () => {
    const id = `rule-${Date.now().toString(36)}`;
    const rule: MessagingRule = kind === 'audience_size_over' ? { id, kind, threshold: Number(threshold), requiresRole: role } : kind === 'channel' ? { id, kind, channel, requiresRole: role } : { id, kind };
    change({ ...policy, rules: [...policy.rules, rule] });
  };
  return (
    <section>
      <h1>Approvals</h1>
      <p className="hint">{view.data.isDefault ? 'Default for this environment kind: production needs approval, other environments do not.' : 'Set for this environment.'}</p>
      <Switch label="Campaigns need an approved revision to launch" checked={policy.campaigns} disabled={!canManage} onCheckedChange={(on) => change({ ...policy, campaigns: on })} />
      <Switch label="Canvas needs an approved version to launch" checked={policy.canvases} disabled={!canManage} onCheckedChange={(on) => change({ ...policy, canvases: on })} />
      <Switch label="Allow approving your own request" checked={policy.allowSelfApprove} disabled={!canManage} onCheckedChange={(on) => change({ ...policy, allowSelfApprove: on })} />
      <h2>Messaging rules</h2>
      {policy.rules.length === 0 ? (
        <p className="hint">No rules: any reviewer may approve.</p>
      ) : (
        <ul aria-label="Messaging rules">
          {policy.rules.map((r) => (
            <li key={r.id}>
              {describe(r)}{' '}
              {canManage && (
                <Button variant="ghost" aria-label={`Remove rule: ${describe(r)}`} onClick={() => change({ ...policy, rules: policy.rules.filter((x) => x.id !== r.id) })}>
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && (
        <div className="filters" role="group" aria-label="New rule">
          <select aria-label="Rule" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="audience_size_over">Audience over</option>
            <option value="channel">Channel</option>
            <option value="transactional_exemption">Transactional campaigns skip approval</option>
          </select>
          {kind === 'audience_size_over' && <input aria-label="Threshold" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value.replace(/\D/g, ''))} />}
          {kind === 'channel' && (
            <select aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
          {kind !== 'transactional_exemption' && (
            <select aria-label="Approver role" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}
          <Button onClick={addRule} disabled={kind === 'audience_size_over' && !Number(threshold)}>
            Add rule
          </Button>
        </div>
      )}
      {save.isError && <p role="alert">{save.error instanceof ApiError ? save.error.message : 'Something went wrong.'}</p>}
    </section>
  );
}
